import React from 'react';
import { FormattedMessage } from 'react-intl';

import {
  PLATFORM_MODE_REBATE, PLATFORM_MODE_TRADE, TRADE_DIRECTION_BUY,
  TRADE_DIRECTION_SELL, TRADE_DIRECTION_UNSPECIFIED,
  TRADE_TYPE_COMMUNITY, TRADE_TYPE_CONTRACTED, TRADE_TYPE_NOMINATED,
  TRADE_TYPE_RESIDUAL,
} from 'src/util/constants';
import { useIntlCurrency } from 'src/util/i18n/currency';
import { getTradeDirection, getTradeDirectionAction } from 'src/util/i18n/helpers';

import { valuePerUnitWithStrong, valueRangePerUnitWithStrong } from './energyPrice';

/**
 * Generates internationalized price information display for trade rules, handling different
 * platform modes (trade vs rebate), pricing structures (flat vs time-of-use), and display
 * contexts (timeline vs standard view).
 *
 * Used throughout the application to consistently format trade rule pricing information
 * in trade rule lists, timelines, active trade displays, and customer proposals.
 * @param {object} params - Function parameters
 * @param {import('react-intl').IntlShape} params.intl - React Intl internationalization object
 *   for formatting messages
 * @param {TRADE_DIRECTION_BUY | TRADE_DIRECTION_SELL} params.direction - Trade
 *   direction from GraphQL schema (TRADE_DIRECTION_BUY or TRADE_DIRECTION_SELL)
 * @param {number} params.maximum - Maximum price value
 * @param {number} params.minimum - Minimum price value
 * @param {PLATFORM_MODE_REBATE|PLATFORM_MODE_TRADE} params.mode - Platform operating
 *   mode determining price display format (rebate shows discounts, trade shows prices)
 * @param {React.ReactElement} [params.detailsLink=null] - Optional React element containing
 *   link to trade rule details page
 * @param {TRADE_TYPE_CONTRACTED | TRADE_TYPE_NOMINATED |
 *   TRADE_TYPE_COMMUNITY|TRADE_TYPE_RESIDUAL} [params.tradeType=null] - Trade
 *   type
 * @param {boolean} [params.timeline=false] - Whether rendering for trade rule timeline view
 * @returns {React.ReactElement|React.ReactNode[]|string|null} Returns FormattedMessage JSX element
 * for standardview with detailsLink support, formatted string with JSX interpolation for timeline
 *  view, italicized JSX for residual rebate mode, or null if no matching conditions
 * @example
 * // Standard trade mode with flat pricing
 * buildTradeRulePriceInformation({
 *   intl,
 *   direction: TRADE_DIRECTION_BUY,
 *   minimum: 0.15,
 *   maximum: 0.15,
 *   mode: PLATFORM_MODE_TRADE,
 *   detailsLink: <Link to="/trade-rules/123">details</Link>
 * });
 * // Returns: <FormattedMessage> "Buy price of 15.000 c/kWh (details)"
 * @example
 * // Rebate mode with time-of-use pricing for timeline
 * buildTradeRulePriceInformation({
 *   intl,
 *   direction: TRADE_DIRECTION_SELL,
 *   minimum: 0.10,
 *   maximum: 0.25,
 *   mode: PLATFORM_MODE_REBATE,
 *   timeline: true
 * });
 * // Returns: "Selling at a discount of 10.000 to 25.000 c/kWh"
 */
export const buildTradeRulePriceInformation = ({
  intl, direction, maximum, minimum, mode, detailsLink = null, tradeType = null, timeline = false,
}) => {
  const intlCurrency = useIntlCurrency();
  const priceAndUnit = valuePerUnitWithStrong(intl, intlCurrency, minimum);
  const priceRange = valueRangePerUnitWithStrong(intl, intlCurrency, { minimum, maximum });
  const tradeDirection = timeline
    ? getTradeDirectionAction(intl, direction) : getTradeDirection(intl, direction);

  if (mode === PLATFORM_MODE_REBATE && tradeType === TRADE_TYPE_RESIDUAL) {
    return (
      <em>
        {intlCurrency.formatMessage({ id: 'trade_rule.i18n.helpers.price_information.agreed_tariffs.label', defaultMessage: 'Agreed energy tariffs apply' })}
      </em>
    );
  }
  if (mode === PLATFORM_MODE_REBATE && minimum === maximum) {
    if (timeline) {
      return intl.formatMessage(
        {
          id: 'trade_rule.i18n.helpers.price_information.flat_price.rebate_mode.timeline.text',
          defaultMessage: '{tradeDirection} at a discount of {priceAndUnit}',
        },
        {
          tradeDirection: <strong>{tradeDirection}</strong>,
          priceAndUnit,
        },
      );
    }

    return (
      <FormattedMessage
        id="trade_rule.i18n.helpers.price_information.flat_price.rebate_mode.text"
        defaultMessage="Discount of {priceAndUnit} {detailsLinkFormatted}"
        values={{
          priceAndUnit,
          detailsLinkFormatted: detailsLink ? (
            <>
              (
              {detailsLink}
              )
            </>
          ) : null,
        }}
      />
    );
  }

  if (mode !== PLATFORM_MODE_REBATE && minimum === maximum) {
    if (timeline) {
      return intl.formatMessage(
        {
          id: 'trade_rule.i18n.helpers.price_information.flat_price.trade_mode.timeline.text',
          defaultMessage: '{tradeDirection} at a price of {priceAndUnit}',
        },
        {
          tradeDirection: <strong>{tradeDirection}</strong>,
          priceAndUnit,
        },
      );
    }

    return (
      <FormattedMessage
        id="trade_rule.i18n.helpers.price_information.flat_price.trade_mode.text"
        defaultMessage="{tradeDirection} price of {priceAndUnit} {detailsLinkFormatted}"
        values={{
          tradeDirection,
          priceAndUnit,
          detailsLinkFormatted: detailsLink ? (
            <>
              (
              {detailsLink}
              )
            </>
          ) : null,
        }}
      />
    );
  }

  if (mode === PLATFORM_MODE_REBATE && minimum !== maximum) {
    if (timeline) {
      return intl.formatMessage(
        {
          id: 'trade_rule.i18n.helpers.price_information.time_of_use_price.rebate_mode.timeline.text',
          defaultMessage: '{tradeDirection} at a discount of {priceRange}',
        },
        {
          tradeDirection: <strong>{tradeDirection}</strong>,
          priceRange,
        },
      );
    }
    return (
      <FormattedMessage
        id="trade_rule.i18n.helpers.price_information.time_of_use_price.rebate_mode.text"
        defaultMessage="Discount of {priceRange} {detailsLinkFormatted}"
        values={{
          priceRange,
          detailsLinkFormatted: detailsLink ? (
            <>
              (
              {detailsLink}
              )
            </>
          ) : null,
        }}
      />
    );
  }

  if (mode !== PLATFORM_MODE_REBATE && minimum !== maximum) {
    if (timeline) {
      return intl.formatMessage(
        {
          id: 'trade_rule.i18n.helpers.price_information.time_of_use_price.trade_mode.timeline.text',
          defaultMessage: '{tradeDirection} at a price of {priceRange}',
        },
        {
          tradeDirection: <strong>{tradeDirection}</strong>,
          priceRange,
        },
      );
    }

    return (
      <FormattedMessage
        id="trade_rule.i18n.helpers.price_information.time_of_use_price.trade_mode.text"
        defaultMessage="{tradeDirection} price of {priceRange}{detailsLinkFormatted}"
        values={{
          tradeDirection,
          priceRange,
          detailsLinkFormatted: detailsLink ? (
            <>
              {' '}
              (
              {detailsLink}
              )
            </>
          ) : null,
        }}
      />
    );
  }

  return null;
};

/**
 * Retrieves the appropriate trade point label based on the given trade direction.
 * @param {import('react-intl').IntlShape} intl - i18n react-intl.
 * @param {TRADE_DIRECTION_BUY | TRADE_DIRECTION_SELL
 * | TRADE_DIRECTION_UNSPECIFIED} direction - The trade direction.
 * @returns {string} The appropriate trade point label.
 */
export const getTradePointLabel = (intl, direction) => {
  let tradePointLabel;

  const tradePointLabelBuy = intl.formatMessage({ id: 'trade_rule.i18n.helpers.trade_point_label.buyer.text', defaultMessage: 'Choose the Trade Point you are buying for' });
  const tradePointLabelSell = intl.formatMessage({ id: 'trade_rule.i18n.helpers.trade_point_label.seller.text', defaultMessage: 'Choose the Trade Point you are selling from' });
  const tradePointLabelGeneral = intl.formatMessage({ id: 'trade_rule.i18n.helpers.trade_point_label.general.text', defaultMessage: 'Choose the Trade Point' });

  switch (direction) {
    case TRADE_DIRECTION_BUY:
      tradePointLabel = tradePointLabelBuy;
      break;
    case TRADE_DIRECTION_SELL:
      tradePointLabel = tradePointLabelSell;
      break;
    default:
      tradePointLabel = tradePointLabelGeneral;
      break;
  }
  return tradePointLabel;
};
